<script lang="ts">
import { h } from 'vue'
import { mapGetters } from 'vuex'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import { defineComponent, inject } from 'vue'
import { i18n } from '@b2ag/locale'

export default defineComponent({
  name: 'PHierarchicalMegaMenu',
  props: {
    categories: {
      type: Array,
    },
    refine: {
      type: Function,
    },
  },
  setup() {
    const getSlugFromCategories = inject('getSlugFromCategories') as ((categories: string[]) => string) | undefined
    return {
      getSlugFromCategories,
    }
  },
  data() {
    return {
      selectedItem: null,
      selectedCategory: null,
      showLeftArrow: false,
      showRightArrow: false,
      cssBase: 'mobile-menu-new',
    }
  },
  computed: {
    ...mapGetters(['getCategories']),
    categoryOrder() {
      const categoryOrder = {}
      const buildCategoryOrder = (categories, parentValue = null) => {
        categories.forEach((category) => {
          const fullValue = parentValue ? `${parentValue} > ${category.label}` : category.label
          categoryOrder[fullValue] = (category.children || []).map((child) => child.label)

          if (category.children) {
            buildCategoryOrder(category.children, fullValue)
          }
        })
      }

      buildCategoryOrder(this.getCategories)
      return categoryOrder
    },
  },
  watch: {
    selectedCategory(newSelectedCategory, oldSelectedCategory) {
      if (newSelectedCategory?.value !== oldSelectedCategory?.value) {
        if (this.$refs.menuContainer) this.$refs.menuContainer.scrollLeft = 0
        setTimeout(() => {
          this.updateArrowVisibility()
        }, 300)
      }
    },
    categories: {
      handler() {
        setTimeout(() => {
          this.updateArrowVisibility()
        }, 300)
      },
      deep: true,
    },
  },
  mounted() {
    this.checkOverflow()
    window.addEventListener('resize', this.checkOverflow)
    const container = this.$refs.menuContainer
    container.addEventListener('scroll', this.updateArrowVisibility)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkOverflow)
    const container = this.$refs.menuContainer
    container.removeEventListener('scroll', this.updateArrowVisibility)
  },
  methods: {
    trackClick(label, value, isRefined) {
      const categories = value.split(' > ')
      let payload = {}

      if (label === i18n._('Tout') || !isRefined) {
        if (categories.length > 1) {
          payload = {
            category_name: categories[categories.length - 2],
            sub_category_name: categories[categories.length - 1],
          }
        } else {
          payload = {
            category_name: categories[0] === '' ? i18n._('Tout') : categories[0],
          }
        }
      } else if (categories.length > 2) {
        payload = {
          category_name: categories[categories.length - 3],
          sub_category_name: categories[categories.length - 2],
        }
      } else {
        payload = {
          category_name: categories[0],
        }
      }
      useAnalyticService().trackEvent('list_menu_click', payload)
    },
    findSelectedItem(category) {
      const item = category.data?.find((subcategory) => subcategory.isRefined)
      return item ?? category
    },
    findSelectedCategory(items) {
      const item = items.find((category) => category.isRefined)
      return item?.data?.length > 0 ? (this.findSelectedCategory(item.data) ?? item) : undefined
    },
    dispatchAndRefine(value, isRefined = false, canDeselect = true) {
      const categories = value.split(' > ')

      if (isRefined && !canDeselect) return

      if (categories.length) {
        this.$store.dispatch('setCategoryLevelRoute', categories)
      }

      const lastCategory = isRefined ? categories[categories.length - 2] : categories[categories.length - 1]

      this.$store.dispatch('setFamily', categories[0])
      if (lastCategory && lastCategory !== categories[0]) {
        this.$store.dispatch('setCategory', lastCategory)
      } else if (lastCategory === undefined) {
        this.$store.dispatch('setFamily', '')
      } else {
        this.$store.dispatch('setCategory', '')
      }

      if (value === '') {
        this.$router.push({ name: 'search', query: { ...this.$route.query } })
      } else {
        const slug = this.getSlugFromCategories?.(categories)
        if (slug) {
          this.$router.push({ name: 'category', params: { slug }, query: { ...this.$route.query } })
        }
      }

      if (!isRefined && !canDeselect && this.selectedItem) {
        this.refine(this.selectedItem.value)
      } else {
        this.refine(value)
      }
    },
    createMenu({ data }) {
      if (data.isRefined && data.data) {
        const sortedData = { ...data, data: this.transformItems(data.data) }
        return [
          this.renderNode({ data: { ...sortedData, label: i18n._('Tout'), canDeselect: false } }),
          ...sortedData.data.map((category) => this.renderNode({ data: category })),
        ]
      }
      return this.renderNode({ data })
    },
    renderNode({ data }) {
      const nodeType = 'leaf'
      const elemClass = [`${this.cssBase}__group-node`]
      const leafClass = [`${this.cssBase}__${nodeType}`]

      const selected = data.isRefined && !data.data?.find((category) => category.isRefined)
      if (selected) {
        elemClass.push(`${this.cssBase}__group-node--selected`)
        leafClass.push(`${this.cssBase}__${nodeType}--selected`)
      }

      const fn = () => this.dispatchAndRefine(data.value, selected, data.canDeselect)
      const trackClick = () => this.trackClick(data.label, data.value, data.isRefined)

      return h('li', { class: elemClass }, [
        h(
          'span',
          {
            onClick() {
              fn()
              trackClick()
            },
            class: leafClass,
          },
          data.label,
        ),
      ])
    },
    renderNavigationArrows() {
      const leftArrow = h(
        'div',
        {
          class: `${this.cssBase}__arrow ${this.cssBase}__arrow--left adn-icon adn-icon--navigation-arrow-back`,
          onClick: [this.scrollLeft, (e) => e.stopPropagation()],
        },
        '',
      )

      const rightArrow = h(
        'div',
        {
          class: `${this.cssBase}__arrow ${this.cssBase}__arrow--right adn-icon adn-icon--navigation-arrow-right`,
          onClick: [this.scrollRight, (e) => e.stopPropagation()],
        },
        '',
      )

      return { left: leftArrow, right: rightArrow }
    },
    scrollLeft() {
      const container = this.$refs.menuContainer as HTMLElement
      if (!container) return
      const maxScrollLeft = Math.min(container.scrollLeft, 300)
      container.scrollLeft -= maxScrollLeft
    },
    scrollRight() {
      const container = this.$refs.menuContainer as HTMLElement
      if (!container) return
      const maxScrollRight = Math.min(container.scrollWidth - container.scrollLeft - container.offsetWidth, 300)
      container.scrollLeft += maxScrollRight
    },
    checkOverflow() {
      const container = this.$refs.menuContainer as HTMLElement
      if (!container) return
      this.showRightArrow = container.offsetWidth < container.scrollWidth
      this.updateArrowVisibility()
    },
    updateArrowVisibility() {
      const container = this.$refs.menuContainer as HTMLElement
      const tolerance = 2
      if (!container) return
      const { scrollLeft } = container
      const maxScrollLeft = container.scrollWidth - container.offsetWidth - tolerance

      this.showLeftArrow = scrollLeft > 0
      this.showRightArrow = scrollLeft < maxScrollLeft
    },
    buildCategoryOrder(categories = this.getCategories, parentValue = null) {
      categories.forEach((category) => {
        const fullValue = parentValue ? `${parentValue} > ${category.label}` : category.label
        this.categoryOrder[fullValue] = (category.children || []).map((child) => child.label)
        if (category.children) {
          this.buildCategoryOrder(category.children, fullValue)
        }
      })
    },
    transformItems(items) {
      const sortCategories = (categories) => {
        if (!this.selectedCategory) return categories
        const orderedLabels = this.categoryOrder[this.selectedCategory.value] || []

        return categories.slice().sort((a, b) => {
          const indexA = orderedLabels.indexOf(a.label)
          const indexB = orderedLabels.indexOf(b.label)
          return indexA - indexB
        })
      }

      return sortCategories(items)
    },
  },
  render() {
    let renderElements
    const rootClass = [`${this.cssBase}__root`]
    this.selectedCategory = this.findSelectedCategory(this.categories)

    if (this.selectedCategory) {
      this.selectedItem = this.findSelectedItem(this.selectedCategory)
      renderElements = this.createMenu({ data: this.selectedCategory })
    } else {
      const hasOtherRefinedCategories = this.categories.some(
        (category) => category.isRefined && category.label !== i18n._('Tout'),
      )

      renderElements = [
        {
          count: 0,
          data: null,
          isRefined: !hasOtherRefinedCategories,
          value: '',
          label: i18n._('Tout'),
          canDeselect: false,
        },
        ...this.categories,
      ].map((data) => this.createMenu({ data }))
    }
    const navigationArrows = this.renderNavigationArrows(h)
    return h('div', { class: this.cssBase }, [
      this.showLeftArrow ? navigationArrows.left : null,
      h('ul', { class: rootClass, ref: 'menuContainer' }, [renderElements]),
      this.showRightArrow ? navigationArrows.right : null,
    ])
  },
})
</script>
<style lang="scss">
.mobile-menu-new {
  position: relative;
  font-size: var(--font-size-m);
  list-style: none;
  margin: 0 -15px;
  padding: 0;
  box-shadow: 0px 1px 3px -1px #14141433;
  border-top: 1px solid var(--color-grey-light);

  @media screen and (min-width: $breakpoint-lg) {
    box-shadow: none;
    margin: 0;
    border-top: none;

    &__root {
      background-color: transparent !important;
      border-bottom: 1px solid var(--color-border-light);
    }

    &__group-node {
      &:hover::after {
        position: relative;
        bottom: 0;
        height: 0.3rem;
        background-color: var(--color-main);
        border-radius: var(--idf-border-radius-m);
        content: '';
        width: 100%;
      }
    }
  }

  &__root {
    scroll-behavior: smooth;
    gap: var(--spacing-2);
    padding-top: var(--spacing-2);

    @media (max-width: $breakpoint-lg) {
      padding-left: var(--spacing-3);
      padding-right: var(--spacing-3);
    }

    background-color: white;
    display: flex;
    overflow-x: auto;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }

    li.mobile-menu-new__group-node--selected::after {
      position: relative;
      bottom: 0;
      height: 0.3rem;
      background-color: var(--color-main);
      border-radius: var(--idf-border-radius-m);
      content: '';
      width: 100%;
    }
  }

  span {
    display: inline-block;
  }

  &__group-node {
    display: flex;
    align-items: center;
    flex-direction: column;
    font-weight: normal;

    &--pointer {
      cursor: pointer;
    }
  }

  &__leaf {
    padding: var(--spacing-2);

    @media screen and (min-width: $breakpoint-lg) {
      padding: 1.3rem;
    }

    cursor: pointer;
    white-space: nowrap;

    &--selected {
      font-weight: bold;
      color: var(--color-main);
    }
  }

  &__arrow {
    display: none;
    position: absolute;
    top: 0;
    bottom: 1px;
    width: 3.5rem;
    align-items: center;
    background-color: var(--color-background);
    color: var(--color-text);
    cursor: pointer;
    z-index: 10;
    transition: font-size var(--idf-transition-speed-2) ease-in-out;

    &--left {
      left: 0;
      justify-content: start;
    }

    &--right {
      right: 0;
      justify-content: end;
    }

    &:hover {
      /* @todo: to update in font-size / icons refactoring user story. */
      font-size: 2.8rem;
    }
  }

  @media screen and (min-width: $breakpoint-lg) {
    &__arrow {
      display: flex;
    }
  }
}
</style>
